/**
 * React Query Provider
 * 
 * Configures and provides the QueryClient to the application.
 * Includes DevTools for debugging in development mode.
 * 
 * @module providers/QueryProvider
 */

import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// =============================================================================
// QUERY CLIENT CONFIGURATION
// =============================================================================

/**
 * QueryClient with optimized default options
 * 
 * Configuration:
 * - staleTime: 5 minutes - Data is fresh for 5 min before refetching
 * - gcTime: 30 minutes - Unused data is garbage collected after 30 min
 * - retry: 1 - Retry failed requests once
 * - refetchOnWindowFocus: true - Refetch when user returns to tab
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      // staleTime: 5 * 60 * 1000,

      // Cache data for 30 minutes
      // gcTime: 30 * 60 * 1000,

      // Retry failed requests once
      retry: 3,

      // Refetch when window regains focus
      refetchOnWindowFocus: true,

      // Refetch on mount if data is stale (default behavior)
      // This ensures invalidated queries are refetched when navigating
      refetchOnMount: true,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

interface QueryProviderProps {
  children: React.ReactNode;
}

/**
 * QueryProvider Component
 * 
 * Wraps the application with React Query's QueryClientProvider.
 * Includes DevTools in development mode for debugging.
 * 
 * @example
 * <QueryProvider>
 *   <App />
 * </QueryProvider>
 */
const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* DevTools only in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom" />
      )}
    </QueryClientProvider>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export { queryClient };
export default QueryProvider;

